import React, { useState } from 'react';
import SurveyRecordsTable from '../SurveyRecordsTable';
import { EnhanceTableAction, IField, IPatient, ISubmission, ISurvey } from '../../../constants/types';
import { Translate } from 'react-localize-redux';
import * as types from "../../../constants";
import Modal from '../../../components/general/modal';
import { Typography } from '@mui/material';
import { HOSPITAL_BILLING_EDIT } from '../../../routes/urls';
import { LinkStyled } from '../../../components/general/mini_components';
import { Link } from 'react-router-dom';
import { BillingInfo } from '../Billing/types';
import SubmissionsTableSelector from './SubmissionsTableSelector';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { SubmissionPDFDocument } from '../../../components/pdf/documents/SubmissionPDFDocument';
import { PDFDownloadButtonWithSignature } from '../../../components/pdf/components/PDFDownloadButtonWithSignature';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { useCurrentInvestigation } from '../../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissionPatientService } from '../../../services';
import { extractPrescribingDoctor } from '../../../utils/doctorUtils';
import { useResearcherRole } from '../../../hooks/useResearcherRole';
import { deleteSubmissionPatientAction, stopTreatmentPatientAction } from '../../../redux/actions/submissionsPatientActions';
import { canDeletePrescriptionFromTable, canAnnulPrescriptionFromTable, canUsePrescriptionRowActions } from './submissionsTableActions';
import { useReasonPrompt, ReasonModal } from './ReasonPromptModal';
import { isSlimSubmission } from '../../../utils/slimSubmissions';
import { treatmentStoppedAtOf } from '../../../utils/treatmentStopped';
import { epochOrIsoToDate } from '../../../utils/index.jsx';

// SHER-1055: the API's refusal of a second stop. errorCode 5 covers every stop
// validation failure, so the message is the only thing that singles this one out.
const API_TREATMENT_ALREADY_STOPPED = 'Treatment already stopped!';

function formatTableDate(date: Date | string) {
    return new Date(date).toLocaleString('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
}

interface SubmissionsTableProps {
    locale:string,
    fieldsSurvey?: IField[],
    surveys: ISurvey[],
    records: ISubmission[],
    category: number,
    patient:IPatient,
    billingInfo: BillingInfo | null,
    onSelectSubmission: (index:number) => void;
}

const SubmissionsTable: React.FC<SubmissionsTableProps> = ({ locale, surveys, patient, billingInfo, category, fieldsSurvey, records, 
                                                        onSelectSubmission }) => {
    const dispatch = useDispatch<any>();
    const [showModalPrintPDF, setShowModalPrintPDF] = useState(false);
    const [submissionPrint, setSubmissionPrint] = useState<ISubmission | null>(null);
    // SHER-537 delete + SHER-570 annul ("arrêter l'ordonnance"): both capture a
    // reason then confirm, via the shared reason-prompt hook/modal.
    const deletePrompt = useReasonPrompt();
    const annulPrompt = useReasonPrompt();

    const currentInvestigationUuid = useSelector((state: any) =>
        state.investigations.currentInvestigation?.uuid
    );
    const permissions = useSelector((state: any) =>
        state.investigations.currentInvestigation?.permissions ?? []
    );
    // SHER-1101: gate the PDF export action behind the tenant functionality.
    const { isPdfExportEnabled } = useCurrentInvestigation();
    
    // Get PDF config and investigation UUID for new PDF component
    const { config: pdfConfig } = usePDFConfig();
    // SHER-224: resolve the AUTHOR's role to label the PDF signature.
    // SHER-569: this read `submissionPrint?.uuidResearcher`, a field the API has
    // never returned — ISubmissionSurvey carries `researcher`, not a flat uuid —
    // so the hook never ran and the title line was silently omitted. Derive the
    // uuid the same way this component already derives the signatory for the PDF
    // itself, so the label and the name cannot describe two different people.
    const prescribingDoctorForRole = extractPrescribingDoctor(submissionPrint || {});
    const prescribingDoctorRole = useResearcherRole({
        uuidInvestigation: currentInvestigationUuid || '',
        uuidResearcher: prescribingDoctorForRole?.uuid,
        enabled: showModalPrintPDF,
    });

    const canDeleteOrAnnulPrescriptions = canUsePrescriptionRowActions({ category, permissions });

    async function printSubmission(submission:ISubmission){
        // SHER-483 review: the list now holds SLIM rows (no surveyRecords), so printing a PDF
        // straight from the list would render an empty document. If the row is slim, fetch the
        // full submission by id before printing.
        let toPrint = submission;
        if(isSlimSubmission(submission as any) && currentInvestigationUuid){
            try {
                const resp:any = await getSubmissionPatientService(currentInvestigationUuid, submission.id);
                if(resp?.submission){
                    toPrint = resp.submission;
                }
            } catch (e) { /* fall back to the slim row rather than blocking the print */ }
        }
        setSubmissionPrint(toPrint);
        setShowModalPrintPDF(true);
    }

    function confirmDelete() {
        if (deletePrompt.pendingIndex === null || !currentInvestigationUuid) {
            return;
        }
        const record = records[deletePrompt.pendingIndex];
        if (!record?.id || !record.uuidSurvey) {
            return;
        }
        deletePrompt.submit({
            requiredKey: 'hospital.patient.submission-table.delete_reason_required',
            action: (reason) => dispatch(deleteSubmissionPatientAction(
                { deletionReason: reason },
                currentInvestigationUuid,
                record.id,
                patient.uuid,
                record.uuidSurvey,
            )),
            // SHER-537: show a clear localized reason inline (CHOM runs in French).
            // errorCode 3 = the intentional 24h immutability window.
            resolveError: (error) => error?.response?.data?.errorCode === 3
                ? 'hospital.patient.submission-table.delete_error_too_old'
                : 'hospital.patient.submission-table.delete_error_general',
        });
    }

    function confirmAnnul() {
        if (annulPrompt.pendingIndex === null || !currentInvestigationUuid) {
            return;
        }
        const record = records[annulPrompt.pendingIndex];
        if (!record?.id || !record.uuidSurvey) {
            return;
        }
        // Whole-ordonnance annul: the API stops every medication line in the
        // submission and keeps it visible, marked stopped. Reuses the inpatient
        // stop action (STOP_TREATMENT-gated).
        annulPrompt.submit({
            requiredKey: 'hospital.patient.submission-table.annul_reason_required',
            action: (reason) => dispatch(stopTreatmentPatientAction(
                { 'treatment-stoppedAt': new Date().toISOString(), 'treatment-stop_motive': reason },
                currentInvestigationUuid,
                patient.uuid,
                record.uuidSurvey,
                record.surveyName,
                record.id,
            )),
            // SHER-572: the reason shows inline in this modal, localized (CHOM runs in
            // French) — never the raw English API message.
            resolveError: (error) => error?.response?.data?.errorMessage === API_TREATMENT_ALREADY_STOPPED
                ? 'hospital.patient.submission-table.annul_error_already_stopped'
                : 'hospital.patient.submission-table.annul_error_general',
        });
    }

    function renderPDFModal(){
        const currentSurvey = surveys.find((survey) => survey.uuid === submissionPrint?.uuidSurvey);
        
        if(!pdfConfig){
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <Typography variant='body2'><Translate id="pages.hospital.patient.fill_billing_info" />, <Link to={HOSPITAL_BILLING_EDIT}><Translate id="general.here" /></Link></Typography>
                </Modal>
            )
        }
        else if(currentSurvey && submissionPrint){
            // Find prescribing doctor from the submission
            const prescribingDoctor = extractPrescribingDoctor(submissionPrint);
            
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <PDFDownloadButtonWithSignature
                            uuidInvestigation={currentInvestigationUuid || ''}
                            uuidResearcher={prescribingDoctor?.uuid}
                            filename={`${currentSurvey.name}-${submissionPrint.id}`}
                            variant="contained"
                            size="large"
                            label={<Translate id="general.download_pdf" />}
                        >
                            {(signatureBase64) => (
                                <SubmissionPDFDocument
                                    submission={submissionPrint}
                                    currentSurvey={currentSurvey}
                                    patient={patient}
                                    prescribingDoctor={prescribingDoctor}
                                    prescribingDoctorRole={prescribingDoctorRole || undefined}
                                    config={pdfConfig}
                                    nameSurvey={currentSurvey.name}
                                    locale={locale}
                                    uuidInvestigation={currentInvestigationUuid || ''}
                                    signatureBase64={signatureBase64}
                                />
                            )}
                        </PDFDownloadButtonWithSignature>
                    </div>
                </Modal>
            )
        }
        
    }
    if(surveys.length > 0 && [types.CATEGORY_DEPARTMENT_NURSE, types.CATEGORY_DEPARTMENT_NURSE_FW].includes(surveys[0].category)){
        return <SubmissionsTableSelector surveys={surveys} records={records}
                    locale={locale} billingInfo={billingInfo}
                    uuidInvestigation={currentInvestigationUuid} patientUuid={patient.uuid}
                    onSelectSubmission={(index) => onSelectSubmission(index)}  />
    }
        
    let headCells = [{ id: "researcher", alignment: "left", label: <Translate id="hospital.created-by" />}, { id: "surveyName", alignment: "left", label: <Translate id="hospital.data-collection" />},
                        { id: "date", alignment: "left", label: "Date"}];
    // SHER-1055: prescriptions get a status column so a stopped ordonnance is visibly
    // stopped, from the slim row after a reload as much as right after the stop.
    const showsTreatmentStatus = category === types.CATEGORY_DEPARTMENT_PRESCRIPTIONS;
    if (showsTreatmentStatus) {
        headCells.push({ id: "status", alignment: "left", label: <Translate id="hospital.patient.submission-table.status_header" /> });
    }
    let actions:EnhanceTableAction[] = [];
    // SHER-1101: only expose the PDF export action when it is enabled for the tenant.
    if (isPdfExportEnabled()) {
        actions.push({
            "type" : "pdf", 
            "func" : (index) => printSubmission(records[index]),
            "check": (row: any) => {
                const record = records[row.id];
                return record && (record as any).type !== "stay";
            }
        });
    }

    // Delete and annul are pushed together and made mutually exclusive per row by
    // their window-aware `check`: delete shows only inside the 24h correction
    // window, annul only after it (both still need the prescriptions category and
    // the right permission — the check returns false otherwise, so no icon renders).
    if (canDeleteOrAnnulPrescriptions) {
        actions.push({
            type: 'delete',
            func: (index) => deletePrompt.open(index),
            check: (row: any) => canDeletePrescriptionFromTable({
                category,
                permissions,
                recordType: (records[row.id] as any)?.type,
                createdAt: records[row.id]?.createdAt,
            }),
        });
        actions.push({
            type: 'stop',
            func: (index) => annulPrompt.open(index),
            check: (row: any) => canAnnulPrescriptionFromTable({
                category,
                permissions,
                recordType: (records[row.id] as any)?.type,
                createdAt: records[row.id]?.createdAt,
                stopped: treatmentStoppedAtOf(records[row.id]) !== null,
            }),
        });
    }

    function renderTreatmentStatus(record: ISubmission) {
        const stoppedAt = treatmentStoppedAtOf(record);
        if (!stoppedAt) {
            return '';
        }
        return (
            <Typography variant="body2" color="error" data-testid="prescription-stopped">
                {/* treatment-stoppedAt is ISO from the API but epoch-ms from an offline
                    optimistic write — epochOrIsoToDate reads both. */}
                <Translate id="hospital.patient.submission-table.prescription_stopped" /> · {formatTableDate(epochOrIsoToDate(stoppedAt))}
            </Typography>
        );
    }

    return(
        <>
            {renderPDFModal()}
            <ReasonModal
                prompt={deletePrompt}
                title={<Translate id="hospital.patient.submission-table.confirm_delete_prescription_title" />}
                body={<Translate id="hospital.patient.submission-table.confirm_delete_prescription" />}
                reasonLabel={<Translate id="hospital.patient.submission-table.delete_reason_label" />}
                confirmLabel="general.delete"
                onConfirm={confirmDelete}
            />
            <ReasonModal
                prompt={annulPrompt}
                title={<Translate id="hospital.patient.submission-table.confirm_annul_prescription_title" />}
                body={<Translate id="hospital.patient.submission-table.confirm_annul_prescription" />}
                reasonLabel={<Translate id="hospital.patient.submission-table.annul_reason_label" />}
                confirmLabel="hospital.patient.submission-table.annul_confirm"
                onConfirm={confirmAnnul}
            />
            <EnhancedTable noHeader noSelectable selectRow={(index:number) => onSelectSubmission(index)} 
                rows={records.map((record, index) => {
                    const dateCreatedString = record.createdAt ? formatTableDate(record.createdAt) : "Unsincronized";
                    const row: any = {id : index, researcher : record.researcher, surveyName : record.surveyName, date : dateCreatedString, type: (record as any).type};
                    if (showsTreatmentStatus) {
                        row.status = renderTreatmentStatus(record);
                    }
                    return row;
                })} headCells={headCells} 
                    actions={actions} /> 
        </>
    );
};

export default SubmissionsTable;
